/**
 * SupportSection — Contact support, developer tools/diagnostics
 */

import React, { useState, useCallback } from 'react';
import { Linking, Switch } from 'react-native';
import {
  VStack,
  HStack,
  Box,
  Text,
  Pressable,
  Button,
  ButtonText,
  ButtonSpinner,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { SectionCard } from '../ui';
import { api } from '../../services/api';
import { errorMessage } from '../../generated/api/endpoint-errors';
import { useUserPreferencesStore } from '../../store/userPreferencesStore';
import { selectDebugContextSnapshots, selectUpdateDebugContextSnapshots, selectLessonFeedbackEnabled, selectUpdateLessonFeedbackEnabled } from '../../store/userPreferencesSelectors';
import { useAppBannerStore } from '../../hooks/useAppBanner';
import { useAsyncAction } from '../../hooks/useAsyncAction';

export const SupportSection = React.memo(function SupportSection() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const showBanner = useAppBannerStore((s) => s.showBanner);

  const debugContextSnapshots = useUserPreferencesStore(selectDebugContextSnapshots);
  const updateDebugContextSnapshots = useUserPreferencesStore(selectUpdateDebugContextSnapshots);
  const lessonFeedbackEnabled = useUserPreferencesStore(selectLessonFeedbackEnabled);
  const updateLessonFeedbackEnabled = useUserPreferencesStore(selectUpdateLessonFeedbackEnabled);

  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const healthCheckAction = useAsyncAction(
    useCallback(async () => {
      const result = await api.checkHealth();
      if (result.ok) return result.data;
      throw new Error(errorMessage(result));
    }, [])
  );

  const handleHealthCheck = () => {
    healthCheckAction.reset();
    healthCheckAction.execute().catch(() => {});
  };

  return (
    <>
      {/* Contact Support */}
      <SectionCard title={t('profile.support.title')}>
        <Pressable
          testID="profile-button-contact-support"
          accessibilityLabel={t('profile.support.contactSupportAccessibility')}
          accessibilityRole="link"
          onPress={() => Linking.openURL('mailto:help@fluencia.io')}
        >
          <HStack
            style={{ backgroundColor: theme.inputBg }}
            p="$4"
            rounded="$lg"
            justifyContent="space-between"
            alignItems="center"
          >
            <VStack>
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
                {t('profile.support.contactSupport')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                help@fluencia.io
              </Text>
            </VStack>
            <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}>
              {t('common.email')}
            </Text>
          </HStack>
        </Pressable>
      </SectionCard>

      {/* Developer Tools */}
      <SectionCard>
        <HStack justifyContent="space-between" alignItems="center">
          <Text allowFontScaling={false} accessibilityRole="header" style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.text, ...ffs }}>
            {t('profile.devTools.title')}
          </Text>
          <Pressable testID="profile-button-diagnostics" accessibilityLabel={showDiagnostics ? t('profile.devTools.hideAccessibility') : t('profile.devTools.showAccessibility')} accessibilityRole="button" onPress={() => setShowDiagnostics(!showDiagnostics)}>
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}>
              {showDiagnostics ? t('common.hide') : t('common.show')}
            </Text>
          </Pressable>
        </HStack>

        {showDiagnostics && (
          <VStack testID="profile-devtools-section" space="md">
            {/* Save Context Snapshots toggle */}
            <HStack justifyContent="space-between" alignItems="center">
              <VStack flex={1} mr="$3">
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
                  {t('profile.learning.debugContextSnapshotsTitle')}
                </Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
                  {t('profile.learning.debugContextSnapshotsDescription')}
                </Text>
              </VStack>
              <Switch
                testID="profile-toggle-debugContextSnapshots"
                accessibilityLabel={t('profile.learning.debugContextSnapshotsTitle')}
                accessibilityRole="switch"
                value={debugContextSnapshots}
                onValueChange={updateDebugContextSnapshots}
              />
            </HStack>

            {/* Lesson Feedback toggle */}
            <HStack justifyContent="space-between" alignItems="center">
              <VStack flex={1} mr="$3">
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
                  {t('profile.learning.lessonFeedbackTitle')}
                </Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
                  {t('profile.learning.lessonFeedbackDescription')}
                </Text>
              </VStack>
              <Switch
                testID="profile-toggle-lessonFeedback"
                accessibilityLabel={t('profile.learning.lessonFeedbackTitle')}
                accessibilityRole="switch"
                value={lessonFeedbackEnabled}
                onValueChange={updateLessonFeedbackEnabled}
              />
            </HStack>

            <Pressable
              testID="profile-button-refresh-all"
              accessibilityLabel={t('profile.devTools.refreshAllData')}
              accessibilityRole="button"
              onPress={() => {
                import('../../services/freshnessManager').then(({ freshnessManager }) => {
                  freshnessManager.refreshAll();
                  showBanner({ message: t('profile.devTools.refreshAllStarted'), type: 'info' });
                });
              }}
            >
              <HStack
                style={{ backgroundColor: theme.inputBg }}
                p="$4"
                rounded="$lg"
                space="sm"
                justifyContent="center"
                alignItems="center"
              >
                <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: colors.primary, fontWeight: '600', ...ffs }}>
                  {t('profile.devTools.refreshAllData')}
                </Text>
              </HStack>
            </Pressable>
            <Button testID="profile-button-health-check" size="md" style={{ backgroundColor: theme.primary }} isDisabled={healthCheckAction.loading} onPress={handleHealthCheck}>
              {healthCheckAction.loading ? (
                <ButtonSpinner color="white" />
              ) : (
                <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }}>{t('profile.devTools.checkBackendHealth')}</ButtonText>
              )}
            </Button>
            {healthCheckAction.error && (
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.error, ...ffs }}>
                {t('profile.devTools.errorPrefix', { message: healthCheckAction.error })}
              </Text>
            )}
            {healthCheckAction.data && (
              <Box testID="profile-health-result" mt="$1" p="$3" style={{ backgroundColor: theme.inputBg }} rounded="$md">
                <VStack space="sm">
                  <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}>
                    {t('profile.devTools.environment')}
                  </Text>
                  <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}>
                    {healthCheckAction.data.environment}
                  </Text>
                  <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }} mt="$2">
                    {t('profile.devTools.commitBranch')}
                  </Text>
                  <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}>
                    {healthCheckAction.data.commit} / {healthCheckAction.data.branch}
                  </Text>
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </SectionCard>
    </>
  );
});
